import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Nav, Container, Offcanvas, Button, Dropdown } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';

const AdminLayout = ({ children }) => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({
    convocatorias: location.pathname.startsWith('/convocatorias') || 
                   location.pathname.startsWith('/cotizaciones') || 
                   location.pathname.startsWith('/evaluacion')
  });

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // EVALUADOR es rol 3, no debe ver el módulo de usuarios
  // Obtener el rol del usuario (puede venir como número o string)
  const userRole = user ? Number(user.rol) : null;
  
  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: 'bi-speedometer2' },
    { 
      path: '/convocatorias', 
      label: 'Convocatorias', 
      icon: 'bi-file-earmark-text',
      children: [
        { path: '/cotizaciones', label: 'Cotizaciones', icon: 'bi-file-earmark-spreadsheet' },
        { path: '/evaluacion', label: 'Evaluación', icon: 'bi-clipboard-check' },
        { path: '/ordenes-notificadas', label: 'Órdenes Notificadas', icon: 'bi-bell' }
      ]
    },
    { path: '/proveedores', label: 'Proveedores', icon: 'bi-shop' }, // Disponible para ADMIN y EVALUADOR
    { path: '/usuarios', label: 'Usuarios', icon: 'bi-people', roles: [1] }, // Solo admin (rol 1)
  ].filter(item => {
    // Si el item tiene restricción de roles, verificar
    if (item.roles && userRole !== null) {
      return item.roles.includes(userRole);
    }
    // Si no tiene restricción, mostrar a todos
    return true;
  });

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const isChildActive = (children) => {
    if (!children) return false;
    return children.some(child => location.pathname === child.path || location.pathname.startsWith(child.path));
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f5f7fa' }}>
      {/* Top Navbar */}
      <Navbar expand="lg" className="shadow-sm" style={{ backgroundColor: '#2b3e53', borderBottom: '3px solid #3498db' }}>
        <Container fluid>
          <Navbar.Brand as={Link} to="/dashboard" className="d-flex align-items-center text-white">
            <div style={{ 
              width: '40px', 
              height: '40px', 
              backgroundColor: '#3498db', 
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px'
            }}>
              <i className="bi bi-clipboard-data text-white fs-5"></i>
            </div>
            <div>
              <span className="fw-bold d-block" style={{ fontSize: '1.1rem' }}>Sistema Cotizador</span>
              <span className="small text-white-50" style={{ fontSize: '0.75rem' }}>DIRESA</span>
            </div>
          </Navbar.Brand>
          
          <Navbar.Toggle 
            aria-controls="sidebar-nav" 
            onClick={() => setShowSidebar(!showSidebar)}
            className="border-0"
            style={{ borderColor: 'rgba(255, 255, 255, 0.5)' }}
          >
            <span className="navbar-toggler-icon" style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba%28255, 255, 255, 0.85%29' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e")` }}></span>
          </Navbar.Toggle>
          
          <Navbar.Collapse className="justify-content-end">
            <Nav>
              <Dropdown align="end">
                <Dropdown.Toggle 
                  variant="light" 
                  id="user-dropdown"
                  className="d-flex align-items-center border-0"
                  style={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    borderRadius: '6px',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}
                >
                  <i className="bi bi-person-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                  <span className="d-none d-md-inline" style={{ fontSize: '0.9rem' }}>
                    {user?.nombre_completo || user?.razon_social || user?.email || 'Usuario'}
                  </span>
                  <i className="bi bi-chevron-down ms-2" style={{ fontSize: '0.7rem' }}></i>
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ minWidth: '220px', marginTop: '0.5rem' }}>
                  <Dropdown.Header>
                    <div className="fw-bold">{user?.nombre_completo || user?.razon_social || user?.email || 'Usuario'}</div>
                    <small className="text-muted">{user?.email}</small>
                  </Dropdown.Header>
                  <Dropdown.Divider />
                  <Dropdown.Item as={Link} to="/perfil">
                    <i className="bi bi-person me-2"></i>
                    Información de Usuario
                  </Dropdown.Item>
                  <Dropdown.Item as={Link} to="/change-password">
                    <i className="bi bi-key me-2"></i>
                    Cambiar Contraseña
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleLogout} className="text-danger">
                    <i className="bi bi-box-arrow-right me-2"></i>
                    Cerrar Sesión
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <div className="d-flex">
        {/* Sidebar - Desktop */}
        <div className="d-none d-lg-block shadow-sm" style={{ 
          width: '260px', 
          minHeight: 'calc(100vh - 56px)', 
          backgroundColor: '#2b3e53',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div className="p-3" style={{ paddingTop: '1.5rem' }}>
            <nav className="nav flex-column">
              {menuItems.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const menuKey = item.path.replace('/', '');
                const isExpanded = expandedMenus[menuKey] || false;
                const isItemActive = isActive(item.path) || (hasChildren && isChildActive(item.children));
                
                return (
                  <div key={item.path}>
                    <div
                      className={`d-flex align-items-center mb-2 rounded text-white fw-medium`}
                      style={{
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        backgroundColor: isItemActive ? '#3498db' : 'transparent',
                        borderRadius: '10px',
                        fontSize: '0.95rem',
                        padding: '0.75rem 1rem',
                        borderLeft: isItemActive ? '4px solid #fff' : '4px solid transparent'
                      }}
                      onClick={() => {
                        if (hasChildren) {
                          toggleMenu(menuKey);
                          // También navegar a la ruta principal
                          navigate(item.path);
                        } else {
                          navigate(item.path);
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isItemActive) {
                          e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.8)';
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isItemActive) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.transform = 'translateX(0)';
                        } else {
                          e.currentTarget.style.backgroundColor = '#3498db';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }
                      }}
                    >
                      <i className={`${item.icon} me-3`} style={{ fontSize: '1.1rem', width: '20px', color: 'white' }}></i>
                      {hasChildren ? (
                        <>
                          <span style={{ color: 'white', flex: 1 }}>{item.label}</span>
                          <i 
                            className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} 
                            style={{ fontSize: '0.8rem', color: 'white' }}
                          ></i>
                        </>
                      ) : (
                        <>
                          <Link
                            to={item.path}
                            style={{ color: 'white', textDecoration: 'none', flex: 1 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {item.label}
                          </Link>
                          {isActive(item.path) && (
                            <span>
                              <i className="bi bi-circle-fill" style={{ fontSize: '6px', color: 'white' }}></i>
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    
                    {/* Submenús */}
                    {hasChildren && isExpanded && (
                      <div style={{ marginLeft: '1.5rem', marginBottom: '0.5rem', borderLeft: '2px solid rgba(255, 255, 255, 0.3)', paddingLeft: '1rem' }}>
                        {item.children.map((child) => {
                          const isChildActiveItem = isActive(child.path);
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              className="d-flex align-items-center mb-2 rounded text-white"
                              style={{
                                transition: 'all 0.2s ease',
                                textDecoration: 'none',
                                backgroundColor: isChildActiveItem ? 'rgba(52, 152, 219, 0.6)' : 'transparent',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                padding: '0.6rem 0.75rem',
                                borderLeft: isChildActiveItem ? '3px solid #fff' : '3px solid transparent'
                              }}
                              onMouseEnter={(e) => {
                                if (!isChildActiveItem) {
                                  e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.4)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isChildActiveItem) {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                } else {
                                  e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.6)';
                                }
                              }}
                            >
                              <i className={`${child.icon} me-2`} style={{ fontSize: '0.95rem', width: '18px', color: 'white' }}></i>
                              <span style={{ color: 'white' }}>{child.label}</span>
                              {isChildActiveItem && (
                                <span className="ms-auto">
                                  <i className="bi bi-circle-fill" style={{ fontSize: '5px', color: 'white' }}></i>
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Sidebar - Mobile (Offcanvas) */}
        <Offcanvas 
          show={showSidebar} 
          onHide={() => setShowSidebar(false)}
          placement="start"
          className="w-auto"
          style={{ backgroundColor: '#2b3e53' }}
        >
          <Offcanvas.Header closeButton className="text-light border-bottom border-secondary">
            <Offcanvas.Title className="text-light">Menú</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="p-0">
            <nav className="nav flex-column p-3">
              {menuItems.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const menuKey = item.path.replace('/', '');
                const isExpanded = expandedMenus[menuKey] || false;
                const isItemActive = isActive(item.path) || (hasChildren && isChildActive(item.children));
                
                return (
                  <div key={item.path}>
                    <div
                      className="d-flex align-items-center py-3 px-3 mb-1 rounded text-white"
                      onClick={() => {
                        if (hasChildren) {
                          toggleMenu(menuKey);
                        } else {
                          navigate(item.path);
                          setShowSidebar(false);
                        }
                      }}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: isItemActive ? '#3498db' : 'transparent',
                        color: 'white',
                        transition: 'all 0.2s',
                        borderLeft: isItemActive ? '4px solid #fff' : '4px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (!isItemActive) {
                          e.target.style.backgroundColor = 'rgba(52, 152, 219, 0.8)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isItemActive) {
                          e.target.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <i className={`${item.icon} me-2 fs-5`} style={{ color: 'white' }}></i>
                      {hasChildren ? (
                        <>
                          <span style={{ color: 'white', flex: 1 }}>{item.label}</span>
                          <i 
                            className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} 
                            style={{ fontSize: '0.8rem', color: 'white' }}
                          ></i>
                        </>
                      ) : (
                        <Link
                          to={item.path}
                          style={{ color: 'white', textDecoration: 'none', flex: 1 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowSidebar(false);
                          }}
                        >
                          {item.label}
                        </Link>
                      )}
                    </div>
                    
                    {/* Submenús móvil */}
                    {hasChildren && isExpanded && (
                      <div style={{ marginLeft: '1rem', marginBottom: '0.5rem', borderLeft: '2px solid rgba(255, 255, 255, 0.3)', paddingLeft: '1rem' }}>
                        {item.children.map((child) => {
                          const isChildActiveItem = isActive(child.path);
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              className="d-flex align-items-center py-2 px-3 mb-1 rounded text-white"
                              onClick={() => setShowSidebar(false)}
                              style={{ 
                                textDecoration: 'none',
                                backgroundColor: isChildActiveItem ? 'rgba(52, 152, 219, 0.6)' : 'transparent',
                                color: 'white',
                                transition: 'all 0.2s',
                                borderLeft: isChildActiveItem ? '3px solid #fff' : '3px solid transparent'
                              }}
                              onMouseEnter={(e) => {
                                if (!isChildActiveItem) {
                                  e.target.style.backgroundColor = 'rgba(52, 152, 219, 0.4)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isChildActiveItem) {
                                  e.target.style.backgroundColor = 'transparent';
                                }
                              }}
                            >
                              <i className={`${child.icon} me-2`} style={{ color: 'white' }}></i>
                              <span style={{ color: 'white' }}>{child.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </Offcanvas.Body>
        </Offcanvas>

        {/* Main Content */}
        <div className="flex-grow-1" style={{ padding: '2rem' }}>
          <div className="fade-in">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;

